import React, { useRef, useEffect } from "react";
import { Switch, Popover } from "antd";
import { Button } from "../buttons";
import { NeurapulseTestCompletionStatusType } from "src/common/Enums";
import NeurapulseIcon from "./NeurapulseIcon";

export interface INeurapulseFilter {
	[keys: string]: any;
	completed: boolean;
	highRisk: boolean;
	incomplete: boolean;
}

export interface IProps {
	name: string;
	values: INeurapulseFilter;
	shouldDisplayFilter: boolean;
	displayFilterName: string;
	closeDisplayFilter: (name: string) => void;
	showOrHideDisplayFilters: (name: string) => void;
	onChange: (item: any) => void;
	convertForFilter: (switchType: string, name: string, onChange: any) => void;
}

// Utility function for mapping filter types
export const mapNeurapulseFilterTypes = (
	status: NeurapulseTestCompletionStatusType
) => {
	switch (status) {
		case NeurapulseTestCompletionStatusType.Completed:
			return "completed";
		case NeurapulseTestCompletionStatusType.Active:
		case NeurapulseTestCompletionStatusType.Confirming:
		case NeurapulseTestCompletionStatusType.NotStarted:
			return "active";
		case NeurapulseTestCompletionStatusType.HighRisk:
			return "highRisk";
		case NeurapulseTestCompletionStatusType.UnknownError:
		case NeurapulseTestCompletionStatusType.UnknownStatus:
		case NeurapulseTestCompletionStatusType.CouldNotCompleteConnectionIssues:
		case NeurapulseTestCompletionStatusType.CouldNotCompleteNpServerIssues:
		case NeurapulseTestCompletionStatusType.CouldNotCompleteTimedOut:
		case NeurapulseTestCompletionStatusType.CouldNotCompleteUnsupportedDevice:
			return "incomoplete";
		default:
			return undefined;
	}
};

export const mapNeurapulseFilterTypesReverse = (filter: string) => {
	switch (filter) {
		case "completed":
			return NeurapulseTestCompletionStatusType.Completed;
		case "active":
			return [
				NeurapulseTestCompletionStatusType.Active,
				NeurapulseTestCompletionStatusType.Confirming,
				NeurapulseTestCompletionStatusType.NotStarted,
			];
		case "highRisk":
			return NeurapulseTestCompletionStatusType.HighRisk;
		case "incomplete":
			return [
				NeurapulseTestCompletionStatusType.UnknownError,
				NeurapulseTestCompletionStatusType.UnknownStatus,
				NeurapulseTestCompletionStatusType.CouldNotCompleteConnectionIssues,
				NeurapulseTestCompletionStatusType.CouldNotCompleteNpServerIssues,
				NeurapulseTestCompletionStatusType.CouldNotCompleteTimedOut,
				NeurapulseTestCompletionStatusType.CouldNotCompleteUnsupportedDevice,
			];
		default:
			return [];
	}
};

const getIconForFilter = (filter: string, style?: any) => {
	switch (filter) {
		case "completed":
			return (
				<NeurapulseIcon
					status={NeurapulseTestCompletionStatusType.Completed}
					size={20}
					style={style}
				/>
			);
		case "highRisk":
			return (
				<NeurapulseIcon
					status={NeurapulseTestCompletionStatusType.HighRisk}
					size={20}
					style={style}
				/>
			);
		case "incomplete":
			return (
				<NeurapulseIcon
					status={NeurapulseTestCompletionStatusType.UnknownError}
					size={20}
					style={style}
				/>
			);
		default:
			return "-";
	}
};

const NeurapulseFilter: React.FC<IProps> = (props) => {
	const {
		name,
		values,
		shouldDisplayFilter,
		displayFilterName,
		showOrHideDisplayFilters,
		convertForFilter,
		onChange,
	} = props;

	const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
	const popoverRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent | TouchEvent) => {
			// Check if click is outside the popover content
			if (
				popoverRef.current &&
				!popoverRef.current.contains(event.target as Node)
			) {
				if (shouldDisplayFilter) {
					props.closeDisplayFilter(displayFilterName);
				}
			}
		};

		if (shouldDisplayFilter) {
			document.addEventListener("mousedown", handleClickOutside);
			document.addEventListener("touchend", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("touchend", handleClickOutside);
		};
	}, [shouldDisplayFilter, props, displayFilterName]);

	const getChecked = (key: string) => {
		return values[key as keyof INeurapulseFilter];
	};

	const parseLabel = (key: string) => {
		switch (key) {
			case "completed":
				return "Completed";
			case "highRisk":
				return "High Risk";
			case "incomplete":
				return "Incomplete";
			default:
				return key.charAt(0).toUpperCase() + key.slice(1);
		}
	};

	const refOnClick = (index: number, e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation(); // Prevent event bubbling
	};

	const switches = Object.keys(values).map((key, index) => {
		const label = parseLabel(key);
		return (
			<div
				className="d-flex"
				key={key}
				ref={(el) => {
					if (el) {
						itemRefs.current[index] = el;
					}
				}}
				onClick={(e) => refOnClick(index, e)}
			>
				{label}
				<Switch
					className="ml-auto pl-3"
					checked={getChecked(key)}
					checkedChildren={getIconForFilter(key)}
					unCheckedChildren={getIconForFilter(key)}
					onChange={() => {
						convertForFilter(key, name, onChange);
					}}
				/>
			</div>
		);
	});

	const getLabelAndSwitch = () => {
		return (
			<div className="d-flex">
				<Popover
					content={<div ref={popoverRef}>{switches}</div>}
					title="Filter by: "
					visible={shouldDisplayFilter}
				>
					<Button
						className="btn-outline-primary btn-block p-2"
						onClick={() => showOrHideDisplayFilters(displayFilterName)}
					>
						{filteredSwitchButton(values)}
					</Button>
				</Popover>
			</div>
		);
	};

	const filteredSwitchButton = (values: INeurapulseFilter) => {
		const getDisplayStyle = (displayed: boolean) => {
			return displayed ? {} : { display: "none" };
		};

		// If no filters are selected, show "All"
		if (!Object.values(values).some((v) => v)) {
			return <span>All</span>;
		}

		return (
			<>
				{getIconForFilter("completed", getDisplayStyle(values.completed))}
				{getIconForFilter("highRisk", getDisplayStyle(values.highRisk))}
				{getIconForFilter("incomplete", getDisplayStyle(values.incomplete))}
			</>
		);
	};

	return <div>{getLabelAndSwitch()}</div>;
};

export default NeurapulseFilter;
